package com.sddamico.components.widgets

import androidx.compose.runtime.Composable
import com.sddamico.SitePalette
import com.sddamico.models.TournamentRefResponse
import com.sddamico.models.TournamentTopResult
import com.sddamico.models.UpcomingMatchResponse
import com.sddamico.state.LocalAuthState
import com.sddamico.util.formatMatchDate
import com.sddamico.util.formatMatchTime
import com.sddamico.util.formatPoints
import com.sddamico.util.importanceLabel
import com.sddamico.util.sportDisplayName
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun TournamentCard(tournament: TournamentRefResponse, showResultsLink: Boolean = false) {
    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(1.25.cssRem)
            .fillMaxWidth()
    ) {
        // Desktop header (MD+)
        Row(
            Modifier.fillMaxWidth().padding(bottom = 0.75.cssRem).displayIfAtLeast(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        tournament.name,
                        Modifier.fontSize(1.0625.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
                    )
                    val imp = importanceLabel(tournament.importance)
                    if (imp.isNotEmpty()) {
                        StatusBadge(imp, SitePalette.accentMuted)
                    }
                }
                SpanText(
                    sportDisplayName(tournament.sport),
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textSecondary)
                )
            }
            Spacer()
            EspnLink(tournament.espnUrl)
            TournamentStatusBadge(tournament.status)
        }

        // Mobile header (<MD)
        Column(Modifier.fillMaxWidth().padding(bottom = 0.75.cssRem).displayUntil(Breakpoint.MD).gap(0.25.cssRem)) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    tournament.name,
                    Modifier.fontSize(1.0625.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
                )
                Spacer()
                TournamentStatusBadge(tournament.status)
            }
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    sportDisplayName(tournament.sport),
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textSecondary)
                )
                val imp = importanceLabel(tournament.importance)
                if (imp.isNotEmpty()) {
                    StatusBadge(imp, SitePalette.accentMuted)
                }
                Spacer()
                EspnLink(tournament.espnUrl)
            }
        }

        // Results page link (logged-in viewers on home dashboard)
        if (showResultsLink && LocalAuthState.current.user != null) {
            Row(
                Modifier.fillMaxWidth().margin(top = (-0.5).cssRem).padding(bottom = 0.4.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Spacer()
                ResultsAnchorLink(tournament.id)
            }
        }

        // Results table
        val results = tournament.topResults
        val upcomingMatches = tournament.upcomingMatches
        val hasContent = !results.isNullOrEmpty() || !upcomingMatches.isNullOrEmpty()

        if (hasContent) {
            ResultsTableHeader()

            val isTennis = tournament.sport != "MENS_GOLF"
            val hasUpcoming = !upcomingMatches.isNullOrEmpty()

            // Upcoming matches section (active tennis tournaments)
            if (hasUpcoming) {
                upcomingMatches!!.forEachIndexed { index, match ->
                    val isLast = index == upcomingMatches.lastIndex && !results.isNullOrEmpty()
                    UpcomingMatchRow(match, thickBorder = isLast)
                }
            }

            // Eliminated/completed results
            if (!results.isNullOrEmpty()) {
                val standingCounts = results.groupingBy { it.standing }.eachCount()

                if (hasUpcoming) {
                    // When upcoming matches are shown, also add scoring/non-scoring divider
                    val scoringResults = results.filter { it.points > 0 }
                    val ownedNonScoring = results.filter { it.points <= 0 && it.clubName != "\u2014" }

                    scoringResults.forEachIndexed { index, result ->
                        val isLastBeforeDivider = index == scoringResults.lastIndex && ownedNonScoring.isNotEmpty()
                        val posLabel = if (isTennis) {
                            tennisRoundLabel(result.standing)
                        } else {
                            if ((standingCounts[result.standing] ?: 1) > 1) "T${result.standing}" else "${result.standing}"
                        }
                        ResultRow(result, posLabel = posLabel, thickBorder = isLastBeforeDivider)
                    }

                    if (ownedNonScoring.isNotEmpty()) {
                        ownedNonScoring.forEach { result ->
                            val posLabel = if (isTennis) {
                                tennisRoundLabel(result.standing)
                            } else {
                                if ((standingCounts[result.standing] ?: 1) > 1) "T${result.standing}" else "${result.standing}"
                            }
                            ResultRow(result, posLabel = posLabel)
                        }
                    }
                } else {
                    // Original behavior: divider between scoring and non-scoring
                    val scoringResults = results.filter { it.points > 0 }
                    val ownedNonScoring = results.filter { it.points <= 0 && it.clubName != "\u2014" }

                    scoringResults.forEachIndexed { index, result ->
                        val isLastBeforeDivider = index == scoringResults.lastIndex && ownedNonScoring.isNotEmpty()
                        val posLabel = if (isTennis) {
                            tennisRoundLabel(result.standing)
                        } else {
                            if ((standingCounts[result.standing] ?: 1) > 1) "T${result.standing}" else "${result.standing}"
                        }
                        ResultRow(result, posLabel = posLabel, thickBorder = isLastBeforeDivider)
                    }

                    if (ownedNonScoring.isNotEmpty()) {
                        ownedNonScoring.forEach { result ->
                            val posLabel = if (isTennis) {
                                tennisRoundLabel(result.standing)
                            } else {
                                if ((standingCounts[result.standing] ?: 1) > 1) "T${result.standing}" else "${result.standing}"
                            }
                            ResultRow(result, posLabel = posLabel)
                        }
                    }
                }
            }
        } else {
            SpanText(
                "No results yet",
                Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem)
            )
        }
    }
}

@Composable
private fun EspnLink(espnUrl: String?) {
    if (espnUrl != null) {
        A(
            href = espnUrl,
            attrs = Modifier
                .fontSize(0.6875.cssRem)
                .fontWeight(FontWeight.Medium)
                .color(SitePalette.textMuted)
                .textDecorationLine(TextDecorationLine.None)
                .padding(right = 0.5.cssRem)
                .styleModifier { property("white-space", "nowrap") }
                .toAttrs {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                }
        ) {
            SpanText("ESPN \u2197")
        }
    }
}

@Composable
private fun ResultsAnchorLink(tournamentId: String) {
    val ctx = rememberPageContext()
    Div(
        Modifier
            .fontSize(0.6875.cssRem)
            .fontWeight(FontWeight.Medium)
            .color(SitePalette.primary)
            .cursor(Cursor.Pointer)
            .onClick {
                ctx.router.tryRoutingTo("/results#tournament-$tournamentId")
            }
            .styleModifier { property("white-space", "nowrap") }
            .toAttrs()
    ) {
        Text("Results ↗")
    }
}

@Composable
private fun ResultsTableHeader() {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.4.cssRem)
            .borderBottom(1.px, LineStyle.Solid, SitePalette.border)
    ) {
        SpanText(
            "POS",
            Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textMuted).minWidth(3.cssRem)
        )
        SpanText(
            "PLAYER",
            Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textMuted).flexGrow(1)
        )
        SpanText(
            "TEAM",
            Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textMuted).minWidth(6.cssRem)
                .styleModifier { property("text-align", "right") }
        )
        SpanText(
            "PTS",
            Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textMuted).minWidth(4.cssRem)
                .styleModifier { property("text-align", "right") }
        )
    }
}

private fun tennisRoundLabel(standing: Int): String = when (standing) {
    1 -> "F (win)"
    2 -> "F"
    3, 4 -> "SF"
    in 5..8 -> "QF"
    in 9..16 -> "R4"
    in 17..32 -> "R3"
    in 33..64 -> "R2"
    in 65..128 -> "R1"
    else -> "$standing"
}

private fun tennisRoundLabelFromName(roundName: String): String = when (roundName) {
    "Final" -> "F"
    "Semifinal" -> "SF"
    "Quarterfinal" -> "QF"
    "Round 4" -> "R4"
    "Round 3" -> "R3"
    "Round 2" -> "R2"
    "Round 1" -> "R1"
    else -> roundName
}

@Composable
private fun ResultRow(result: TournamentTopResult, posLabel: String, thickBorder: Boolean = false) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.4.cssRem)
            .borderBottom(if (thickBorder) 3.px else 1.px, LineStyle.Solid, SitePalette.border),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            posLabel,
            Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.Medium)
                .color(SitePalette.textSecondary).minWidth(3.cssRem)
                .styleModifier { property("font-variant-numeric", "tabular-nums") }
        )
        SpanText(
            result.playerName,
            Modifier.fontSize(0.8125.cssRem).color(SitePalette.text).flexGrow(1)
        )
        SpanText(
            result.clubName,
            Modifier.fontSize(0.8125.cssRem).color(SitePalette.textSecondary).minWidth(6.cssRem)
                .styleModifier { property("text-align", "right") }
        )
        SpanText(
            formatPoints(result.points),
            Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.SemiBold)
                .color(SitePalette.accent).minWidth(4.cssRem)
                .styleModifier { property("text-align", "right"); property("font-variant-numeric", "tabular-nums") }
        )
    }
}

@Composable
private fun UpcomingMatchRow(match: UpcomingMatchResponse, thickBorder: Boolean = false) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.4.cssRem)
            .borderBottom(if (thickBorder) 3.px else 1.px, LineStyle.Solid, SitePalette.border),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left: two-line player matchup (POS, PLAYER, CLUB columns)
        Column(Modifier.flexGrow(1)) {
            // Line 1: round label, player1, club1
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    tennisRoundLabelFromName(match.round),
                    Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.Medium)
                        .color(SitePalette.textSecondary).minWidth(3.cssRem)
                )
                SpanText(
                    match.player1Name,
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.text).flexGrow(1)
                )
                SpanText(
                    match.player1ClubName,
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textSecondary).minWidth(6.cssRem)
                        .styleModifier { property("text-align", "right") }
                )
            }
            // Line 2: indented "vs" + player2, club2
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "",
                    Modifier.minWidth(3.cssRem) // spacer matching POS column
                )
                Row(Modifier.flexGrow(1).gap(0.25.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        "vs",
                        Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted)
                            .fontWeight(FontWeight.Medium)
                    )
                    SpanText(
                        match.player2Name ?: "TBD",
                        Modifier.fontSize(0.8125.cssRem)
                            .color(if (match.player2Name != null) SitePalette.text else SitePalette.textMuted)
                    )
                }
                SpanText(
                    match.player2ClubName ?: "\u2014",
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textSecondary).minWidth(6.cssRem)
                        .styleModifier { property("text-align", "right") }
                )
            }
        }
        // Right: time/date or LIVE badge, vertically centered across both lines
        Div(
            Modifier.minWidth(4.cssRem).styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "flex-end")
                property("flex-shrink", "0")
            }.toAttrs()
        ) {
            if (match.state == "in") {
                StatusBadge("LIVE", SitePalette.statusActive)
            } else if (match.scheduledTime != null) {
                SpanText(
                    formatMatchTime(match.scheduledTime),
                    Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted)
                )
                SpanText(
                    formatMatchDate(match.scheduledTime),
                    Modifier.fontSize(0.625.cssRem).color(SitePalette.textMuted)
                )
            }
        }
    }
}
